import * as React from 'react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { ValueDescription } from '../../../value-description/value-description';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { routes } from '../../../../../../config/routes';
import { CardDashboardTradesLoading } from '../card-loading/card-dashboard-trades-loading';
import { IconArrowRightWide } from '../../../../icons/arrow-right-wide';
import { IconButton } from '../../../../icon/button/icon-button';
import { ValueAmount } from '../../../../value/amount/value-amount';
import { ValuePercents } from '../../../../value/percents/value-precents';
import { CardTitle } from '../../../title/card-title';
import { Badge } from '../../../../badge/badge';
import { ReturnMetricEnum } from '../../../../../../models/enums/return-metric-enum';
import { DashboardReturnMetricSwitcher } from '../../../../dashboard/return-metric/dashboard-return-metric-switcher';
import {
    getStoredReturnMetric,
    RETURN_METRIC_STORAGE_KEYS,
} from '../../../../dashboard/return-metric/dashboard-return-metric';
import { Stack } from '../../../../stack/stack';
import { UnrealizedProfitTooltipContent } from '../../../../dashboard/return-metric/unrealized-profit-tooltip-content';
import { formatDate } from '../../../../../../models/functions/format-date';
import { PortfolioTypeEnum } from '../../../../../../defs/api';

type CardDashboardTradesUnrealizedProfitType = {
    shared?: boolean;
};

export const CardDashboardTradesUnrealizedProfit: React.FC<CardDashboardTradesUnrealizedProfitType> = observer(
    ({ shared = false }: CardDashboardTradesUnrealizedProfitType) => {
        const { t } = useTranslation();
        const router = useRouter();
        const [selectedMetric, setSelectedMetric] = useState<ReturnMetricEnum>(ReturnMetricEnum.MoneyWeightedReturn);

        const rootStore = useRootStore();
        const { portfolioStore, statisticStore } = rootStore;
        const { overviewStore } = statisticStore;
        const { overview } = overviewStore;

        const portfolioCurrency = portfolioStore.selectedPortfolio.portfolioHistoryCurrencyCode;
        const isSummaryPortfolio = portfolioStore.selectedPortfolio.type === PortfolioTypeEnum.Summary;
        const useLegacySummaryMetricLock =
            process.env.NEXT_PUBLIC_DISABLE_SUMMARY_TWR_2038 === 'true' && isSummaryPortfolio;
        const todayProfit = statisticStore.ratiosStore.unrealizedTodayProfit.totalTodayProfit;
        const profit = (overview?.unrealizedProfit ?? 0) + todayProfit;
        const todayProfitBase = (overview?.portfolioValue ?? 0) - todayProfit;
        const todayProfitPercentage = todayProfitBase !== 0 ? (todayProfit / todayProfitBase) * 100 : 0;

        useEffect(() => {
            setSelectedMetric(
                getStoredReturnMetric(
                    RETURN_METRIC_STORAGE_KEYS.unrealizedProfit,
                    ReturnMetricEnum.MoneyWeightedReturn,
                ),
            );
        }, []);

        useEffect(() => {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(RETURN_METRIC_STORAGE_KEYS.unrealizedProfit, selectedMetric);
            }
        }, [selectedMetric]);

        // TODO(#2038): Remove this legacy summary metric lock together with NEXT_PUBLIC_DISABLE_SUMMARY_TWR_2038 after testing.
        const effectiveMetric = useLegacySummaryMetricLock ? ReturnMetricEnum.SimpleReturn : selectedMetric;
        let selectedMetricValue = overview?.moneyWeightedReturn ?? 0;
        const showStaticMetricDate = effectiveMetric !== ReturnMetricEnum.SimpleReturn;

        if (effectiveMetric === ReturnMetricEnum.SimpleReturn) {
            selectedMetricValue = overview?.simpleReturn ?? 0;
        } else if (effectiveMetric === ReturnMetricEnum.TimeWeightedReturn) {
            selectedMetricValue = overview?.timeWeightedReturn ?? 0;
        }

        // TODO: Use the date of the latest aggregated return point once the API exposes it.
        const returnMetricDate = overview?.lastUpdated ? formatDate(overview.lastUpdated) : '';

        return (
            <CardDashboardTradesLoading fields={['unrealizedProfit', 'unrealizedProfitPercentage']}>
                <div className="relative flex h-full flex-col items-start overflow-hidden rounded-large bg-surface p-[1.6rem] shadow-strong lg:p-[2.2rem]">
                    <CardTitle
                        title={t('layout.statisticsCard.unrealizedProfit.title')}
                        tooltip={
                            <UnrealizedProfitTooltipContent
                                description={t('layout.statisticsCard.unrealizedProfit.tooltipDescription')}
                            />
                        }
                        compact
                        actions={
                            shared || (
                                <IconButton
                                    icon={<IconArrowRightWide />}
                                    variation={'outlined'}
                                    size={'small'}
                                    onClick={async () => {
                                        await router.push(`${routes.app.profits}`);
                                    }}
                                />
                            )
                        }
                    />
                    {overview && (
                        <Stack direction="vertical" gap={'xxsmall'} fullWidth>
                            <Badge variant={todayProfit > 0 ? 'positive' : 'negative'}>
                                <Trans
                                    i18nKey="layout.statisticsCard.unrealizedProfit.todayProfit"
                                    components={{
                                        value: (
                                            <ValueAmount
                                                amount={todayProfit}
                                                currency={portfolioCurrency}
                                                mode="pure"
                                                hidable
                                            />
                                        ),
                                        percentage: (
                                            <ValuePercents amount={todayProfitPercentage} mode="pure" inParentheses />
                                        ),
                                    }}
                                />
                            </Badge>
                            <ValueDescription
                                padding={'none'}
                                value={<ValueAmount amount={profit} currency={portfolioCurrency} mode="pure" hidable />}
                                description={
                                    <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-[0.3rem] rounded-full border border-border bg-box p-[0.3rem] pl-[1rem]">
                                        <div className="flex min-w-0 flex-wrap items-center gap-xsmall gap-y-[0.1rem] text-small leading-[1.4rem] text-text-muted">
                                            <span className="whitespace-nowrap">
                                                <ValuePercents amount={selectedMetricValue} mode="pure" />
                                            </span>
                                            {showStaticMetricDate && returnMetricDate && (
                                                <span className="whitespace-nowrap">k {returnMetricDate}</span>
                                            )}
                                        </div>
                                        <div className="shrink-0">
                                            <DashboardReturnMetricSwitcher
                                                value={effectiveMetric}
                                                onChange={setSelectedMetric}
                                                size="small"
                                                disabled={useLegacySummaryMetricLock}
                                                disabledTooltip={t('pages.index.summaryReturnMetricsUnavailable')}
                                            />
                                        </div>
                                    </div>
                                }
                                valueColor={profit > 0 ? 'positive' : 'negative'}
                            />
                        </Stack>
                    )}
                </div>
            </CardDashboardTradesLoading>
        );
    },
);
